import ConfirmationModal from "components/ConfirmationModal";
import DocumentViewerModal from "components/DocumentViewer";
import { StatusChip } from "components/StatusChip";
import { useAuth } from "contexts/AuthContext";
import { useTranslation } from "contexts/i18nContext";
import { useToast } from "contexts/ToastContext";
import { useHasRole } from "contexts/AuthContext";
import { useState } from "react";
import { editComplaint } from "services/prosecutorDecipilineService";
import { Complaints, Investigation } from "types";
import {
  COMPLIANT_STATUS,
  FEEDBACK_TYPE,
  INVESTIGATION_STATUS,
} from "utils/constantValues";
import { ROLES } from "utils/roles";
import AddChargeFeedbackModal from "./AddChargeFeedbackModal";
import AddChargeRegistrationModal from "./AddChargeRegistrationModal";
import UpdateChargeDocModal from "./UpdateChargeDocModal";

interface ChargeProps {
  investigationData: Investigation;
  complaintData: Complaints;
  onInvestigationRefresh: any;
  onComplaintRefresh?: any;
}

export default function ChargeDocumentCard({
  investigationData,
  complaintData,
  onInvestigationRefresh,
  onComplaintRefresh,
}: ChargeProps) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { showToast } = useToast();

  const canCreateChargeFeedback = useHasRole([
    ROLES.INSPECTION_COORDINATOR,
    ROLES.INSPECTION_DIRECTOR,
  ]);
  const canViewChargeFeedback = useHasRole([
    ROLES.INSPECTION_DIRECTOR,
    ROLES.INSPECTION_COORDINATOR,
    ROLES.INSPECTION_PROSECUTOR,
  ]);
  const [isSubmitLoading, setIsSubmitLoading] = useState(false);

  const canRegisterChargeDoc = useHasRole([ROLES.INSPECTION_PROSECUTOR]);
  const canUpdateChargeDoc = useHasRole([ROLES.INSPECTION_PROSECUTOR]);

  const [isFeedbackOpen, setIsFeedbackOpen] = useState(false);
  const [forwardReturned, setForwardReturned] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [isUpdateOpen, setIsUpdateOpen] = useState(false);
  const [feedbackExpanded, setFeedbackExpanded] = useState(true);
  const [historyExpanded, setHistoryExpanded] = useState(true);
  const [framedExpanded, setFramedExpanded] = useState(true);

  const handleForwardReturned = async () => {
    setIsSubmitLoading(true);

    try {
      const resData = await editComplaint({
        ...complaintData,
        status: COMPLIANT_STATUS.DRAFT_CHARGE,
      });

      if (
        resData &&
        investigationData?.status ===
          INVESTIGATION_STATUS.FORWARDED_TO_REGISTRAR
      ) {
        await editComplaint({
          ...(complaintData as Complaints),
          has_councile: true,
          status: COMPLIANT_STATUS.COUNCIL_OFFICE,
        });
      }

      onComplaintRefresh && onComplaintRefresh();
      setForwardReturned(false);
      showToast("Case forwarded successfully", "success");
    } catch (err: any) {
      console.error("Submission error:", err);
      showToast("Failed to forward case", "error");
    } finally {
      setIsSubmitLoading(false);
    }
  };

  if (
    !investigationData ||
    (investigationData &&
      !(
        investigationData?.investigation_documents ||
        investigationData?.documents ||
        []
      )?.filter((i) => i?.document_type === "charge_document")?.length &&
      !investigationData?.charge_summary)
  )
    return null;

  const chargeDocuments = (
    investigationData?.investigation_documents ||
    investigationData?.documents ||
    []
  )?.filter((i) => i?.document_type === "charge_document");

  // Sort charge documents by created_at DESC and split into latest + history
  const sortedChargeDocs = [...(chargeDocuments || [])].sort(
    (a: any, b: any) =>
      new Date(b?.created_at ?? "").getTime() -
      new Date(a?.created_at ?? "").getTime(),
  );
  const latestChargeDoc = sortedChargeDocs?.[0];
  const chargeDocHistory = sortedChargeDocs?.slice(1) || [];

  return (
    <div className="mb-8">
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden mb-4">
        <div className="bg-red-800 px-6 py-4 border-b border-red-900">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-lg font-semibold text-white">
                Charge Document
              </h2>
              <p className="text-sm text-red-200 mt-1">
                Case #{complaintData?.complaint_number}
              </p>
            </div>
            <div className="flex items-center gap-2">
              {investigationData?.feedback_status_charge_docu && (
                <StatusChip
                  status={investigationData.feedback_status_charge_docu}
                />
              )}
            </div>
          </div>
        </div>

        <div className="p-6">
          {/* Framed Charge Document Header */}
          <div className="mb-6 border-b border-gray-100 pb-4">
            <button
              onClick={() => setFramedExpanded(!framedExpanded)}
              className="w-full text-left focus:outline-none rounded"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 flex items-center justify-center bg-red-100 border border-red-200 rounded-lg">
                    <svg
                      className="w-5 h-5 text-red-600"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                      />
                    </svg>
                  </div>
                  <div>
                    <h3 className="text-base font-semibold text-gray-800">
                      Framed Charge Document
                    </h3>
                    <p className="text-sm text-gray-500">
                      Legal charge framework and specifications
                    </p>
                  </div>
                </div>
                <svg
                  className={`w-5 h-5 text-gray-500 transform transition-transform ${framedExpanded ? "rotate-180" : "rotate-0"}`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
              </div>
            </button>
          </div>

          {framedExpanded && (
            <>
              {/* Latest Charge Document */}
              {latestChargeDoc && (
                <div className="mb-6">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="text-sm font-semibold text-gray-700">
                      Latest Version
                    </h4>
                    <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-green-100 text-green-800">
                      Current
                    </span>
                  </div>
                  <DocumentViewerModal
                    documents={[latestChargeDoc] as any}
                    baseUrl={`${process.env.REACT_APP_API_BASE_URL}prosecutor-decipiline/investigation-documents/document`}
                    layout="list"
                  />
                </div>
              )}

              {/* Registration Information */}
              <div className="mb-6 bg-gray-50 border border-gray-200 rounded-lg p-4">
                <h4 className="text-sm font-semibold text-gray-700 mb-3">
                  Registration Details
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {investigationData?.date && (
                    <div className="space-y-1">
                      <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Registered Date
                      </p>
                      <p className="text-sm font-medium text-gray-800">
                        {investigationData?.date || "-"}
                      </p>
                    </div>
                  )}

                  {investigationData?.registration_number && (
                    <div className="space-y-1">
                      <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Registration Number
                      </p>
                      <p className="text-sm font-medium text-gray-800">
                        {investigationData?.registration_number || "-"}
                      </p>
                    </div>
                  )}
                </div>
              </div>

              {/* Charge Summary */}
              <div className="mb-6">
                <h4 className="text-sm font-semibold text-gray-700 mb-2">
                  Charge Summary
                </h4>
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                  <p className="text-sm text-gray-700 leading-relaxed">
                    {investigationData?.charge_summary || "No summary provided"}
                  </p>
                </div>
              </div>

              {/* Article of Charge */}
              <div className="mb-6">
                <h4 className="text-sm font-semibold text-gray-700 mb-2">
                  Article of Charge
                </h4>
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                  <p className="text-sm text-gray-700 leading-relaxed">
                    {investigationData?.charge_article ||
                      "No article specified"}
                  </p>
                </div>
              </div>

              {/* Document History */}
              {chargeDocHistory.length > 0 && (
                <div className="mb-6 border-t border-gray-200 pt-6">
                  <button
                    onClick={() => setHistoryExpanded(!historyExpanded)}
                    className="w-full text-left focus:outline-none mb-3"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <svg
                          className="w-4 h-4 text-gray-500"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                          />
                        </svg>
                        <h4 className="text-sm font-semibold text-gray-700">
                          Previous Framed Charge Versions (
                          {chargeDocHistory.length})
                        </h4>
                      </div>
                      <svg
                        className={`w-4 h-4 text-gray-600 transform transition-transform ${historyExpanded ? "rotate-180" : "rotate-0"}`}
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M19 9l-7 7-7-7"
                        />
                      </svg>
                    </div>
                  </button>
                  {historyExpanded && (
                    <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                      <DocumentViewerModal
                        documents={chargeDocHistory as any}
                        baseUrl={`${process.env.REACT_APP_API_BASE_URL}prosecutor-decipiline/investigation-documents/document`}
                        layout="list"
                      />
                    </div>
                  )}
                </div>
              )}

              {/* Feedback Section */}
              <div className="border-t border-gray-200 pt-6">
                <button
                  onClick={() => setFeedbackExpanded(!feedbackExpanded)}
                  className="w-full text-left focus:outline-none mb-4"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <svg
                        className="w-4 h-4 text-gray-500"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z"
                        />
                      </svg>
                      <h4 className="text-sm font-semibold text-gray-700">
                        Review Feedback
                      </h4>
                    </div>
                    <svg
                      className={`w-4 h-4 text-gray-600 transform transition-transform ${feedbackExpanded ? "rotate-180" : "rotate-0"}`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 9l-7 7-7-7"
                      />
                    </svg>
                  </div>
                </button>

                {feedbackExpanded && (
                  <>
                    {/* Director Feedback */}
                    {canViewChargeFeedback &&
                      investigationData?.director_feedbackshargedocument
                        ?.sort(
                          (a: any, b: any) =>
                            new Date(b?.created_at ?? "").getTime() -
                            new Date(a?.created_at ?? "").getTime(),
                        )
                        ?.map((feedback, index) => (
                          <div key={index} className="mb-4 last:mb-0">
                            <div className="bg-red-50 border border-red-100 rounded-lg p-4">
                              <div className="flex items-center justify-between mb-3">
                                <div className="flex items-center gap-2">
                                  <span className="text-sm font-medium text-gray-700">
                                    Director Feedback {index + 1}
                                  </span>
                                </div>
                                {index === 0 && (
                                  <StatusChip
                                    status={
                                      investigationData?.feedback_status_charge_docu ??
                                      null
                                    }
                                  />
                                )}
                              </div>
                              <p className="text-sm text-gray-700 leading-relaxed">
                                {feedback?.comment || "No comment provided"}
                              </p>
                            </div>
                          </div>
                        ))}

                    {/* Council Charge Feedback */}
                    {investigationData?.councilwritercharge_docs?.map(
                      (feedback, index) => (
                        <div key={index} className="mb-4 last:mb-0">
                          <div className="bg-blue-50 border border-blue-100 rounded-lg p-4">
                            <div className="flex items-center gap-2 mb-3">
                              <svg
                                className="w-4 h-4 text-blue-600"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
                                />
                              </svg>
                              <span className="text-sm font-medium text-gray-700">
                                Council Office Feedback {index + 1}
                              </span>
                            </div>
                            <p className="text-sm text-gray-700 leading-relaxed">
                              {feedback?.comment || "No comment provided"}
                            </p>
                          </div>
                        </div>
                      ),
                    )}

                    {/* Action Buttons */}
                    <div className="mt-6 space-y-3">
                      {canCreateChargeFeedback &&
                        investigationData &&
                        complaintData.status === COMPLIANT_STATUS.RETURNED && (
                          <button
                            className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-red-700 text-white text-sm font-medium rounded-md hover:bg-red-800 transition-colors shadow-sm"
                            onClick={() => setForwardReturned(true)}
                          >
                            <svg
                              className="w-4 h-4"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M14 5l7 7m0 0l-7 7m7-7H3"
                              />
                            </svg>
                            Forward to Next Stage
                          </button>
                        )}

                      {canCreateChargeFeedback &&
                        user?.admin_unit === complaintData?.admin_unit_id &&
                        investigationData &&
                        investigationData?.feedback_status_charge_docu !==
                          FEEDBACK_TYPE?.APPROVED && (
                          <button
                            className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-gradient-to-r from-red-700 to-red-600 hover:from-red-800 hover:to-red-700 text-white font-medium rounded-md transition-colors shadow-sm"
                            onClick={() => setIsFeedbackOpen(true)}
                          >
                            <svg
                              className="w-4 h-4"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z"
                              />
                            </svg>
                            Add Feedback
                          </button>
                        )}
                    </div>
                  </>
                )}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex justify-end space-x-3">
        {canRegisterChargeDoc &&
          investigationData?.director_feedbackshargedocument?.some(
            (feedback) => feedback.comment !== null,
          ) &&
          (investigationData?.status === INVESTIGATION_STATUS.ON_CHARGE ||
            investigationData?.status ===
              INVESTIGATION_STATUS.FORWARDED_TO_REGISTRAR) &&
          investigationData?.feedback_status_charge_docu === "Approve" &&
          (!investigationData?.registration_number ||
            (investigationData?.registration_number &&
              complaintData?.status === COMPLIANT_STATUS.DRAFT_CHARGE)) && (
            <button
              className="px-6 py-2.5 bg-red-700 text-white text-sm font-medium rounded-md hover:bg-red-800 transition-colors shadow-sm flex items-center gap-2"
              type="submit"
              onClick={() => setIsOpen(true)}
            >
              <svg
                className="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              Add Registration Number
            </button>
          )}

        {canUpdateChargeDoc &&
          investigationData?.feedback_status === FEEDBACK_TYPE?.APPROVED &&
          (investigationData?.status ===
            INVESTIGATION_STATUS.FORWARDED_TO_REGISTRAR ||
            investigationData?.status ===
              INVESTIGATION_STATUS.BACK_TO_PROSECUTOR) &&
          complaintData.status !== COMPLIANT_STATUS.RETURNED && (
            <button
              className="px-6 py-2.5 bg-red-700 text-white text-sm font-medium rounded-md hover:bg-red-800 transition-colors shadow-sm flex items-center gap-2"
              type="submit"
              onClick={() => setIsUpdateOpen(true)}
            >
              <svg
                className="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                />
              </svg>
              Update Charge Document
            </button>
          )}
      </div>

      {/* Modals */}
      {investigationData && (
        <AddChargeFeedbackModal
          isOpen={isFeedbackOpen}
          setIsOpen={setIsFeedbackOpen}
          complaintData={complaintData}
          investigationData={investigationData}
          onRefresh={onInvestigationRefresh}
          returned={complaintData.status === COMPLIANT_STATUS.RETURNED}
        />
      )}

      {investigationData && (
        <AddChargeRegistrationModal
          isOpen={isOpen}
          setIsOpen={setIsOpen}
          complaintData={complaintData}
          investigationData={investigationData}
          onRefresh={onInvestigationRefresh}
          latestChargeDoc={latestChargeDoc}
        />
      )}

      {investigationData && (
        <UpdateChargeDocModal
          isOpen={isUpdateOpen}
          setIsOpen={setIsUpdateOpen}
          complaintData={complaintData}
          investigationData={investigationData}
          onRefresh={onInvestigationRefresh}
        />
      )}

      <ConfirmationModal
        isOpen={forwardReturned}
        onCancel={() => setForwardReturned(false)}
        onConfirm={handleForwardReturned}
        isLoading={isSubmitLoading}
      />
    </div>
  );
}
